/**
 * The ONE customer-phone normaliser (board 2089 M1, R-1).
 *
 * Three hand-rolled copies used to parse the same population of numbers with
 * three different answers: server/inquiry.ts (public intake), core/data/
 * customers-repo.ts (the kiosk customer key) and notify/driver-message.ts (a
 * digit stripper that fails open). Every customer phone now parses HERE, once,
 * under ONE rule, and the three are callers.
 *
 * Lives in src/shared, not src/core, on the shared/eircode.ts precedent: the
 * kiosk client imports only from src/shared, and the country picker (M3) and
 * the site form (M4) consume the same list and the same formatter.
 *
 * ⛔ src/shared is bundled into the CLIENT and is a SEC-1/SEC-2 sink root for
 * scripts/check-copy.sh: no console call, no dash characters in a string, and
 * nothing here ever logs a number.
 *
 * `libphonenumber-js/min` is named explicitly. The root import resolves to
 * min today; naming it pins the bundle cost (min: 29,844 B gzip, measured
 * with `esbuild --bundle --minify` in docs/research/ecmb-phone-capture-
 * research.md section 4) so a metadata upgrade is a deliberate edit here and
 * not a side effect of a package default.
 *
 * ⛔ NEVER THROWS. Probed on 1.13.7 (research section 10): the library throws
 * on a non-string text ("A text for parsing must be a string"), and the
 * default `extract: true` lifts `+353871234567` out of
 * `<script>+353871234567</script>` and calls it valid. Both doors this feeds
 * are trust boundaries, so the input is typed, trimmed and length-bounded
 * BEFORE the library sees it, `extract` is off, and a refusal is a fixed
 * enum that never carries the typed text.
 */
import {
  parsePhoneNumberFromString,
  isSupportedCountry,
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js/min';

/** The region a bare national number is read in when no country is chosen. */
export const PHONE_DEFAULT_COUNTRY: CountryCode = 'IE';

/**
 * The longest raw string the library is allowed to see. It is the public
 * intake's own bound (`InquirySchema.phone` is `.max(40)` in
 * server/inquiry.ts), restated here so the kiosk door and the notify sink,
 * which have no zod in front of them, are bounded the same way.
 */
export const PHONE_RAW_MAX = 40;

export type PhoneCountry = CountryCode;

const ISO_ALPHA2 = /^[A-Z]{2}$/;

/**
 * STRICT: an upper-case ISO 3166-1 alpha-2 code the library's metadata knows.
 * Never case-folded and never an object. Probed on 1.13.7: the library
 * accepts a `{defaultCountry}` OBJECT as a region, and a lower-case `'ie'`
 * makes `parsePhoneNumberFromString` answer `undefined` rather than parse.
 * A caller that holds a lower-case code (the M2 CSV parser) uppercases
 * before asking; this predicate does not do it for them, because "is this a
 * country" and "make this a country" are two different questions at a trust
 * boundary.
 */
export function isPhoneCountry(v: unknown): v is PhoneCountry {
  return typeof v === 'string' && ISO_ALPHA2.test(v) && isSupportedCountry(v);
}

/**
 * ⛔ A FIXED ENUM. This is the only vocabulary a refusal carries: it rides
 * `inquiries.extra` on the public path and it is the whole of what a log
 * line or a test may say about a number that did not parse. The typed text
 * never appears in a reason.
 */
export const PHONE_REFUSALS = ['empty', 'too_long', 'unparseable', 'invalid_for_country'] as const;
export type PhoneRefusal = (typeof PHONE_REFUSALS)[number];

export type PhoneParseResult =
  | { ok: true; e164: string; country: PhoneCountry | null }
  | { ok: false; reason: PhoneRefusal; country: PhoneCountry };

/**
 * Parse a customer-typed phone under a chosen country.
 *
 *   - `raw` that is not a string       -> `unparseable` (the library would throw)
 *   - trimmed empty                    -> `empty`
 *   - longer than PHONE_RAW_MAX        -> `too_long`, without calling the library
 *   - `country` not a PhoneCountry     -> read as PHONE_DEFAULT_COUNTRY (never a refusal:
 *                                         the public intake never blocks a lead on
 *                                         a contact-format problem)
 *   - nothing the library can read     -> `unparseable`
 *   - reads but `!isValid()`           -> `invalid_for_country` (never `isPossible()`:
 *                                         the Ofcom drama range `07700 900123`
 *                                         is possible and not valid, and a
 *                                         quote SMS to it reaches nobody)
 *
 * A typed `+` or `00` prefix overrides the chosen country, so `+44...` under
 * IE answers GB; that is the library's rule and the one the picker relies on.
 * `country` on the ok arm is the PARSED number's country, which is null for a
 * non-geographic `+800...` number.
 */
export function parseCustomerPhone(raw: unknown, country?: unknown): PhoneParseResult {
  const region: PhoneCountry = isPhoneCountry(country) ? country : PHONE_DEFAULT_COUNTRY;
  if (typeof raw !== 'string') return { ok: false, reason: 'unparseable', country: region };
  const text = raw.trim();
  if (text === '') return { ok: false, reason: 'empty', country: region };
  if (text.length > PHONE_RAW_MAX) return { ok: false, reason: 'too_long', country: region };
  try {
    const parsed = parsePhoneNumberFromString(text, { defaultCountry: region, extract: false });
    if (parsed === undefined) return { ok: false, reason: 'unparseable', country: region };
    if (!parsed.isValid()) return { ok: false, reason: 'invalid_for_country', country: region };
    return { ok: true, e164: parsed.number, country: parsed.country ?? null };
  } catch {
    // The library documents `parsePhoneNumberFromString` as non-throwing for
    // a string, and the guards above keep anything else away from it. The
    // catch is the intake's floor all the same: a throw here would 500 the
    // public form and lose the lead, which is the one thing R-1 must never do.
    return { ok: false, reason: 'unparseable', country: region };
  }
}

/** The ISO country of a stored E.164; null when it does not parse or is non-geographic. */
export function phoneCountryOf(e164: string): PhoneCountry | null {
  const r = parseCustomerPhone(e164);
  return r.ok ? r.country : null;
}

let regionNames: Intl.DisplayNames | null | undefined;

/**
 * The English region name for an ISO code, or the code itself when the
 * platform cannot name it. Guarded because `Intl.DisplayNames` is a runtime
 * capability (Safari 14.1 and later on the kiosk iPad; every Node this repo
 * pins), not a language feature, and a picker that throws on an old device
 * is worse than one that shows "GB".
 */
function regionName(code: PhoneCountry): string {
  try {
    if (regionNames === undefined) {
      regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    }
    return regionNames?.of(code) ?? code;
  } catch {
    regionNames = null;
    return code;
  }
}

export type PhoneDisplay = {
  /** `+44 7400 123456` for a number that parses; the input, trimmed, when it does not. */
  international: string;
  country: PhoneCountry | null;
  countryName: string | null;
};

/**
 * The ONE read-back rendering of a stored E.164 (R-7): international spacing,
 * the parsed country and its name. A value that does not parse is echoed
 * trimmed with no country, so a display site never drops what it was given.
 */
export function formatPhoneForDisplay(e164: string): PhoneDisplay {
  const r = parseCustomerPhone(e164);
  if (!r.ok) {
    return { international: typeof e164 === 'string' ? e164.trim() : '', country: null, countryName: null };
  }
  // A number that parsed is re-read for its international form; the second
  // parse cannot refuse what the first accepted (same text, same rule).
  const parsed = parsePhoneNumberFromString(r.e164, { extract: false });
  return {
    international: parsed?.formatInternational() ?? r.e164,
    country: r.country,
    countryName: r.country === null ? null : regionName(r.country),
  };
}

/**
 * The English name of a CHOSEN country — a picker value, an `extra.phoneCountry`
 * off an inquiry — or the code itself when the platform cannot name it.
 *
 * ⚠️ NOT the same question as `formatPhoneForDisplay().countryName`, which is
 * `string | null` because it names the country of a PARSED NUMBER and a number
 * can have none (it refused, or it is a non-geographic `+800…`). This one always
 * answers, because its input is already a country. The refusal copy on the kiosk
 * capture is built from THIS one, so a refused number can still say which
 * country it was read under.
 */
export function phoneCountryName(code: PhoneCountry): string {
  return regionName(code);
}

/**
 * The ONE read-back line for a stored E.164 (board 2089 M3, R-7):
 * `+353 85 123 4567 (Ireland)`.
 *
 * ⛔ THE PARENTHESIS IS DROPPED, NEVER EMPTIED. `countryName` is `string | null`
 * — null for a value that did not parse (echoed trimmed) and for a
 * non-geographic number — so a naive template renders `+800 1234 5678 ()` or the
 * word null on the screen Helen reads a number off. The bare international form
 * is the honest degradation.
 */
export function formatPhoneWithCountry(e164: string): string {
  const d = formatPhoneForDisplay(e164);
  return d.countryName === null ? d.international : `${d.international} (${d.countryName})`;
}

export type PhoneCountryOption = {
  code: PhoneCountry;
  /** The dialling prefix without the `+`: `353` for IE. */
  callingCode: string;
  name: string;
};

let countryOptions: readonly PhoneCountryOption[] | undefined;

/**
 * Every country the metadata can parse, named, sorted by name, with
 * PHONE_DEFAULT_COUNTRY first. DERIVED from `getCountries()`, never a literal
 * list, so the picker can only ever offer what `parseCustomerPhone` accepts.
 * Computed once per process: the input is static.
 */
export function listPhoneCountries(): readonly PhoneCountryOption[] {
  if (countryOptions !== undefined) return countryOptions;
  const rest: PhoneCountryOption[] = [];
  let first: PhoneCountryOption | undefined;
  for (const code of getCountries()) {
    const option: PhoneCountryOption = { code, callingCode: getCountryCallingCode(code), name: regionName(code) };
    if (code === PHONE_DEFAULT_COUNTRY) first = option;
    else rest.push(option);
  }
  rest.sort((a, b) => a.name.localeCompare(b.name, 'en'));
  countryOptions = first === undefined ? rest : [first, ...rest];
  return countryOptions;
}
